import React, { useState, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import messages from './messages'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Modal, Button, Checkbox, Chip, Field, Input, LoadingFlex } from '@arnsbogroup/react-voicearchive'
import TaskTypeQuantitySelect, { expandTaskTypes } from 'components/TaskTypeQuantitySelect'

export default NiceModal.create(({ order, refetch }) => {
  const modal = useModal()
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [jobs, setJobs] = useState([])
  const [versionIds, setVersionIds] = useState([])
  const [types, setTypes] = useState([])
  const [inheritPurchaseOrder, setInheritPurchaseOrder] = useState(true)
  const [customerReference, setCustomerReference] = useState('')
  const versionsRef = useRef(null)

  useEffect(() => {
    fetch(`/rest/v1/order/${order.uid}?includeRelations=1&includeTaskTree=1`)
      .then((response) => response.json())
      .then(({ tasks = [] }) => {
        setJobs(tasks.filter(({ versions = [] }) => versions.length))
        setLoading(false)
      })
      .catch((error) => {
        toastr.error(error)
        setLoading(false)
      })
  }, [])

  const allVersionIds = jobs.flatMap(({ versions = [] }) => versions.map(({ uid }) => uid))
  const tasksToCreate = versionIds.length * expandTaskTypes(types).length

  const toggleVersion = (uid) => setVersionIds((current) =>
    current.includes(uid)
      ? current.filter((id) => id !== uid)
      : [...current, uid]
  )

  const toggleJob = (versions) => {
    const uids = versions.map(({ uid }) => uid)
    const allChecked = uids.every((uid) => versionIds.includes(uid))

    setVersionIds((current) => allChecked
      ? current.filter((uid) => !uids.includes(uid))
      : [...new Set([...current, ...uids])]
    )
  }

  const toggleAll = () => setVersionIds((current) =>
    current.length === allVersionIds.length ? [] : allVersionIds
  )

  const onSubmit = () => {
    if (saving || !versionIds.length || !types.length) {
      return
    }

    setSaving(true)

    fetch(`/rest/v2/task/createMultipleTaskForVersions`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orderId: order.uid,
        versionIds,
        taskTypes: expandTaskTypes(types),
        inheritCustomerReference: inheritPurchaseOrder,
        customerReference: inheritPurchaseOrder ? '' : customerReference.trim(),
      }),
    })
      .then((response) => response.json())
      .then(({ success, errors = [], data = {} }) => {
        if (!success) {
          toastr.error(errors.join(', '))
          setSaving(false)
          return
        }

        toastr.success(formatMessage(messages.created, { count: data.tasks?.length || 0 }))
        modal.resolve()
        modal.remove()
        refetch()
      })
      .catch((error) => {
        toastr.error(error)
        setSaving(false)
      })
  }

  return (
    <Modal
      show={modal.visible}
      onClose={() => modal.remove()}
      onOutsideClick={() => modal.remove()}
      onSubmit={onSubmit}
      title={formatMessage(messages.title)}
      dialogPanelClasses="max-w-lg"
    >
      <div className="space-y-4">
        { loading ? (
          <LoadingFlex />
        ) : !allVersionIds.length ? (
          <div className="text-sm text-gray-500">
            { formatMessage(messages.noVersions) }
          </div>
        ) : (
          <>
            <Field
              ref={versionsRef}
              label={formatMessage(messages.versions)}
              description={formatMessage(messages.versionsDescription)}
            >
              <div ref={versionsRef} className="border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-64 overflow-y-auto">
                { jobs.map((job) => (
                  <div key={job.uid} className="py-2 px-2 space-y-1">
                    <Checkbox
                      label={job.title || 'Untitled job'}
                      labelClassNames="text-sm font-medium truncate"
                      checked={job.versions.every(({ uid }) => versionIds.includes(uid))}
                      onChange={() => toggleJob(job.versions)}
                    />

                    <div className="pl-6 space-y-1">
                      { job.versions.map((version) => (
                        <div key={version.uid} className="flex items-center justify-between gap-2">
                          <Checkbox
                            label={version.title || version.targetlanguage || `Version ${version.uid}`}
                            labelClassNames="text-sm truncate"
                            checked={versionIds.includes(version.uid)}
                            onChange={() => toggleVersion(version.uid)}
                          />

                          <div className="flex items-center gap-1 shrink-0">
                            { (version.tasks || []).map((task) => (
                              <Chip key={task.uid} size="xs">
                                { task.type?.shortname || '?' }
                              </Chip>
                            )) }
                          </div>
                        </div>
                      )) }
                    </div>
                  </div>
                )) }
              </div>

              <Button variant="inline" size="xs" onClick={toggleAll}>
                { versionIds.length === allVersionIds.length
                  ? formatMessage(messages.deselectAll)
                  : formatMessage(messages.selectAll) }
              </Button>
            </Field>

            <TaskTypeQuantitySelect
              value={types}
              onChange={setTypes}
              description={formatMessage(messages.taskTypesDescription)}
            />

            <Checkbox
              onChange={() => setInheritPurchaseOrder(!inheritPurchaseOrder)}
              checked={inheritPurchaseOrder}
              labelClassNames="text-sm"
              label={`Inherit '${order.customerReference}' P.O. from order`}
            />

            { !inheritPurchaseOrder &&
              <Input
                optional
                label={formatMessage(messages.purchaseOrder)}
                description={formatMessage(messages.purchaseOrderDescription)}
                size="xs"
                value={customerReference}
                onChange={(event) => setCustomerReference(event.target.value)}
              />
            }

            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                { formatMessage(messages.summary, {
                  tasks: tasksToCreate,
                  versions: versionIds.length,
                }) }
              </span>

              <Button
                size="sm"
                disabled={saving || !versionIds.length || !types.length}
                onClick={onSubmit}
              >
                { saving ? formatMessage(messages.adding) : formatMessage(messages.add) }
              </Button>
            </div>
          </>
        ) }
      </div>
    </Modal>
  )
})
